import {
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog,
    handleClose,
    PageRoute,
    pn,
    ScreenType,
    Stack,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
    ValidationErrors,
} from "@compass/core";
import {
    Permission,
    Class,
    ClassEditResource,
    ClassViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { BasicInfo } from "./BasicInfo";
import { ClassPageHeader } from "./ClassPageHeader";

const routeName = "class";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

export const ClassPageRoute: PageRoute<Params> = {
    handle: "Classes",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
};

export const ClassPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params } = useParams(routeName, Params);
    const [classItem, setClassItem] = useState<Class>({} as Class);
    const [initClassItem, setInitClassItem] = useState<Class>(classItem);
    const [viewResource, setViewResource] = useState<ClassViewResource>({});
    const [editResource, setEditResource] = useState<ClassEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [classItem] });
    const { validationErrors, setValidationErrors } = useValidationErrors();

    useEffect(() => {
        fetch(params.id);

        ApiService.classes.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.classes.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    useEffect(() => {
        if (params.crudMode !== "edit" && Object.keys(validationErrors).length) {
            setValidationErrors({});
        }
    }, [params.crudMode, validationErrors, setValidationErrors]);

    const validate = useCallback(() => {
        setValidationErrors({});
        return true;
    }, [setValidationErrors]);


    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${ClassPageRoute.getPath({ id, crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        ApiService.classes.get(id).then((response) => {
            setClassItem(response);
            setInitClassItem(response);
            resetUnsavedChanges([response]);
        });
    };

    const pageHeader = (
        <ClassPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={ClassPageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            classItem={classItem}
            setClassItem={setClassItem}
            initClassItem={initClassItem}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
            onValidate={validate}   
        />
    );

    const pageBody = (
        <Stack spacing="lg">
            <BasicInfo
                crudMode={params.crudMode}
                classItem={classItem}
                setClassItem={setClassItem}
                viewResource={viewResource}
                editResource={editResource}
                validationErrors={validationErrors}
            />
        </Stack>
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};
